import type { FormField } from '@lucid/lego';
import type { RegistrationFormDefaultValues } from './Registration';
import { getRegistrationStatusOptions, getDeliveryTimeOptions } from './constants';

/**
 * Creates form fields configuration for Registration Details form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for registration fields
 * @param vehicleReadyToScheduleDate - Vehicle ready to schedule date for validation
 * @returns Array of FormField configurations
 */
export const createRegistrationFormFields = (
  labels?: Record<string, string>,
  defaultValues?: RegistrationFormDefaultValues,
  vehicleReadyToScheduleDate?: string
): FormField[] => {
  // Calculate minimum date for scheduledDeliveryDate
  // Must be more than both vehicleReadyToScheduleDate and today
  const getMinScheduledDate = (): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let minDate = today;

    if (vehicleReadyToScheduleDate) {
      const readyDate = new Date(vehicleReadyToScheduleDate);
      readyDate.setHours(0, 0, 0, 0);

      // Use the later of vehicleReadyToScheduleDate or today
      if (readyDate > today) {
        minDate = readyDate;
      }
    }

    // Add 1 day to ensure it's "more than" not "equal to or greater than"
    minDate.setDate(minDate.getDate() + 1);

    return minDate.toISOString().split('T')[0];
  };

  const minScheduledDeliveryDate = vehicleReadyToScheduleDate ? getMinScheduledDate() : undefined;

  return [
    {
      id: 'firstRegistrationGroup',
      fieldType: 'group',
      title: labels?.['firstRegistration.details'] ?? 'First Registration Details',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'firstRegistrationDate',
          fieldType: 'date_picker',
          name: 'firstRegistrationDate',
          apiKey: 'firstRegistrationDate',
          label: labels?.['firstRegistration.date'] ?? 'First Registration Date',
          placeholder: labels?.['date.placeholder'] ?? 'Select a date',
          required: false,
          width: 'third',
          defaultValue: defaultValues?.firstRegistrationDate,
        },
        {
          id: 'licensePlateNumber',
          fieldType: 'text',
          name: 'licensePlateNumber',
          apiKey: 'licensePlateNumber',
          label: labels?.['licensePlate.number'] ?? 'License Plate Number',
          placeholder: labels?.['licensePlate.placeholder'] ?? 'Enter license plate number',
          required: false,
          width: 'third',
          defaultValue: defaultValues?.licensePlateNumber,
        },
        {
          id: 'registrationStatus',
          fieldType: 'dropdown',
          name: 'registrationStatus',
          apiKey: 'registrationStatus',
          label: labels?.['registration.status'] ?? 'Registration Status',
          placeholder: labels?.['status.placeholder'] ?? 'Select a status',
          required: false,
          width: 'third',
          options: getRegistrationStatusOptions(labels),
          defaultValue: defaultValues?.registrationStatus,
        },
      ],
    },
    {
      id: 'scheduledDeliveryGroup',
      fieldType: 'group',
      title: labels?.['scheduledDelivery.dates'] ?? 'Scheduled Delivery Dates',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'scheduledDeliveryDate',
          fieldType: 'date_picker',
          name: 'scheduledDeliveryDate',
          apiKey: 'scheduledDeliveryDate',
          label: labels?.['scheduledDelivery.date'] ?? 'Scheduled Delivery Date',
          placeholder: labels?.['date.placeholder'] ?? 'Select a date',
          // Note: Both required and disabled when vehicleReadyToScheduleDate is missing.
          // This is intentional - it blocks form submission until the prerequisite
          // (Vehicle Ready to Schedule Date) is met, providing clear user feedback.
          required: !vehicleReadyToScheduleDate,
          width: 'half',
          defaultValue: defaultValues?.scheduledDeliveryDate,
          minDate: minScheduledDeliveryDate,
          disabled: !vehicleReadyToScheduleDate,
          validationMessages: {
            required:
              labels?.['vehicleReadyToSchedule.required'] ??
              'Vehicle Ready to Schedule Date needs to be populated',
          },
        },
        {
          id: 'scheduledDeliveryTime',
          fieldType: 'dropdown',
          name: 'scheduledDeliveryTime',
          apiKey: 'scheduledDeliveryTime',
          label: labels?.['scheduledDelivery.time'] ?? 'Scheduled Delivery Time',
          placeholder: labels?.['time.placeholder'] ?? 'Select a time',
          required: false,
          width: 'half',
          options: getDeliveryTimeOptions(labels),
          defaultValue: defaultValues?.scheduledDeliveryTime,
          disabled: !vehicleReadyToScheduleDate,
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.['form.save'] ?? 'Save',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'light',
          isFluid: true,
        },
      ],
    },
  ];
};
